import type { ItemIcons } from "../../render/item-icons.ts";
import { h } from "../kit/dom.ts";
import { keyHint } from "./settings.ts";
import type { TradeRow } from "./trade-model.ts";

interface RowView {
  element: HTMLElement;
  icon: HTMLElement;
  text: HTMLElement;
  info: HTMLElement;
  button: HTMLButtonElement;
  row: TradeRow;
}

/**
 * Comércio com o mascate (doc 03): o que ele vende e o que ele compra, com preço, estoque e o
 * saldo da bolsa. Só envia intenções; o servidor confere distância, estoque, saldo e espaço. As
 * linhas são mantidas por chave e atualizadas no lugar (recriar o botão sob o ponteiro perde o clique).
 */
export class TradePanel {
  readonly element: HTMLElement;
  private readonly title: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly closedNote: HTMLElement;
  private readonly sellBox: HTMLElement;
  private readonly buyBox: HTMLElement;
  private readonly rows = new Map<string, RowView>();
  private readonly icons: ItemIcons;
  private readonly send: (side: "comprar" | "vender", index: number) => void;
  /** Mascate aberto (id da estrutura). */
  id: string | undefined;

  constructor(parent: HTMLElement, icons: ItemIcons, send: (side: "comprar" | "vender", index: number) => void) {
    this.icons = icons;
    this.send = send;
    this.title = h("h2", {}, "Mercador");
    this.purse = h("p", { class: "bolsa" });
    this.closedNote = h("p", { class: "hint aviso" }, "Atacado, o mercador não negocia até o amanhecer.");
    this.sellBox = h("div", { class: "craft-list" });
    this.buyBox = h("div", { class: "craft-list" });
    this.element = h(
      "section",
      { id: "mascate", class: "panel crafting trade", "aria-label": "Mercador" },
      this.title,
      this.purse,
      this.closedNote,
      h("h3", {}, "À venda"),
      this.sellBox,
      h("h3", {}, "Compra"),
      this.buyBox,
      keyHint("interact", "{} fecha · o estoque é da partida inteira"),
    );
    this.element.hidden = true;
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  show(id: string | undefined): void {
    this.id = id;
    this.element.hidden = id === undefined;
  }

  update(title: string, closed: boolean, coins: number, rows: readonly TradeRow[]): void {
    if (this.title.textContent !== title) this.title.textContent = title;
    const purse = `Bolsa: ${coins} ${coins === 1 ? "moeda" : "moedas"}`;
    if (this.purse.textContent !== purse) this.purse.textContent = purse;
    this.closedNote.hidden = !closed;
    const keep = new Set<string>();
    for (const row of rows) {
      keep.add(row.key);
      let view = this.rows.get(row.key);
      if (!view) {
        view = this.createRow(row);
        this.rows.set(row.key, view);
      }
      view.row = row;
      view.icon.style.backgroundImage = `url(${this.icons.url(row.item)})`;
      if (view.text.textContent !== row.text) view.text.textContent = row.text;
      const info = `${row.side === "comprar" ? "por" : "paga"} ${row.price} · estoque ${row.stock}`;
      if (view.info.textContent !== info) view.info.textContent = info;
      view.button.disabled = row.blocked !== undefined;
      view.button.title = row.blocked ?? "";
    }
    for (const [key, view] of this.rows) {
      if (keep.has(key)) continue;
      view.element.remove();
      this.rows.delete(key);
    }
    const order = (side: string) => rows.filter((r) => r.side === side).map((r) => this.rows.get(r.key)!.element);
    syncChildren(this.sellBox, order("comprar"));
    syncChildren(this.buyBox, order("vender"));
  }

  private createRow(row: TradeRow): RowView {
    const icon = h("div", { class: "icon" });
    const text = h("div", { class: "name" });
    const info = h("div", { class: "needs" });
    const button = h("button", { type: "button", "data-oferta": row.key }, row.side === "comprar" ? "Comprar" : "Vender");
    const view: RowView = { element: h("div", { class: "recipe" }, icon, text, info, button), icon, text, info, button, row };
    button.addEventListener("click", () => this.send(view.row.side, view.row.index));
    return view;
  }

  remove(): void {
    this.element.remove();
  }
}

function syncChildren(parent: HTMLElement, children: readonly HTMLElement[]): void {
  const current = parent.children;
  const same = current.length === children.length && children.every((c, i) => current[i] === c);
  if (!same) parent.replaceChildren(...children);
}
